/**
 * Tri chronologique des messages : source unique de vérité (client, serveur, PDF).
 *
 * Les dates de messages sont des chaînes (colonne CSV « YYYY-MM-DD HH:mm:ss » ou
 * champ String Mongo pour les messages édités / ajoutés). Une date vide ou mal
 * formée est donc toujours possible en base.
 *
 * IMPORTANT (anti-régression) :
 *   - `new Date('') - new Date('...')` vaut NaN. Un comparateur qui renvoie NaN
 *     désorganise TOUT le tableau (V8 TimSort), pas seulement le message fautif :
 *     un seul message sans date suffisait à sortir juillet avant mars dans le PDF.
 *   - Les dates invalides sont donc isolées et renvoyées en fin de liste, sans
 *     jamais renvoyer NaN.
 *   - Tri décoré (une seule conversion de date par message au lieu d'une par
 *     comparaison) : critique sur les conversations à 200 000 messages.
 *   - Tri stable via l'index d'origine, et copie du tableau : ne mute jamais
 *     l'état Redux passé en argument.
 */

// Timestamp d'un message, ou null si la date est absente / invalide.
export function getMessageTime(message) {
	if(!message || !message.date) {
		return null;
	}
	const time = new Date(message.date).getTime();
	return Number.isNaN(time) ? null : time;
}

export function isMessageDateValid(message) {
	return getMessageTime(message) !== null;
}

// Une date de conversation (dates.start / dates.end / block.start / block.end)
// n'est utilisable que si elle est parseable ET hors epoch. new Date(null)
// vaut 1970-01-01 : le merge posait ça quand le dernier message n'avait pas
// de date (overlays Mongo, lignes CSV pourries). Aucun export SMS n'est antérieur
// à 1990.
export const CONVERSATION_DATE_MIN_MS = Date.UTC(1990, 0, 1);

export function getConversationBoundTime(value) {
	if(value == null || value === '') {
		return null;
	}
	const time = new Date(value).getTime();
	if(Number.isNaN(time) || time < CONVERSATION_DATE_MIN_MS) {
		return null;
	}
	return time;
}

export function isConversationBoundDateValid(value) {
	return getConversationBoundTime(value) !== null;
}

export function getValidDateBoundsFromMessages(messages = []) {
	let startMs = null;
	let endMs = null;
	(messages || []).forEach(message => {
		const time = getConversationBoundTime(message && message.date);
		if(time == null) {
			return;
		}
		if(startMs == null || time < startMs) {
			startMs = time;
		}
		if(endMs == null || time > endMs) {
			endMs = time;
		}
	});
	return {
		start: startMs == null ? null : new Date(startMs),
		end: endMs == null ? null : new Date(endMs),
	};
}

// Bornes affichage / livre : si la paire stockée est cassée (end epoch, end < start),
// on recalcule start ET end depuis les blocs. Garder seulement start (ex. 2025) et
// jeter end (1970) masquerait l'autre conversation fusionnée plus ancienne.
// Écart max entre l'heure écrite et l'instant enregistré à l'import (UTC+14),
// par pas de 15 min (Inde, Népal, etc.).
const IMPORTED_CLOCK_SHIFT_MAX_MS = 14 * 60 * 60 * 1000;
const IMPORTED_CLOCK_SHIFT_STEP_MS = 15 * 60 * 1000;

/**
 * Heure écrite dans le message, sans fuseau.
 * `2026-09-30 14:30:28`, `2026-09-30 14:30:28+04:00` et `2026-09-30T14:30:28.000Z`
 * valent tous 14:30:28. Le suffixe de zone est ignoré.
 * Un Date est lu par ses composants UTC : les bornes réalignées et les dates
 * enregistrées via moment.utc(horloge) stockent l'heure écrite ainsi.
 */
export function getWrittenClockMs(value) {
	if(value == null || value === '') {
		return null;
	}
	if(value instanceof Date) {
		if(Number.isNaN(value.getTime())) {
			return null;
		}
		return Date.UTC(
			value.getUTCFullYear(),
			value.getUTCMonth(),
			value.getUTCDate(),
			value.getUTCHours(),
			value.getUTCMinutes(),
			value.getUTCSeconds(),
		);
	}
	const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
	if(!match) {
		return null;
	}
	return Date.UTC(
		parseInt(match[1], 10),
		parseInt(match[2], 10) - 1,
		parseInt(match[3], 10),
		parseInt(match[4], 10),
		parseInt(match[5], 10),
		parseInt(match[6] || '0', 10),
	);
}

/**
 * Les bornes de bloc sont des instants (`moment(heure écrite).toDate()`).
 * Les messages gardent l'heure écrite, sans zone. On ramène les bornes à
 * cette heure écrite pour que le livre compare des horloges, pas des fuseaux.
 * Une plage déjà identique à l'heure écrite est renvoyée telle quelle.
 * Une plage choisie (début de journée, raccourci) ne correspond à aucun
 * message : elle n'est pas modifiée.
 */
// Écart entre l'heure écrite d'un message et l'instant enregistré pour la borne
// de début à l'import. 0 si les deux disent déjà la même heure.
export function getImportedClockShiftMs(start, sampleDate) {
	const startMs = getConversationBoundTime(start);
	const clockMs = getWrittenClockMs(sampleDate);
	if(startMs == null || clockMs == null) {
		return 0;
	}
	const delta = clockMs - startMs;
	if(
		delta === 0
		|| Math.abs(delta) > IMPORTED_CLOCK_SHIFT_MAX_MS
		|| delta % IMPORTED_CLOCK_SHIFT_STEP_MS !== 0
	) {
		return 0;
	}
	return delta;
}

export function realignImportedClockBounds(start, end, messages = []) {
	const startMs = getConversationBoundTime(start);
	const endMs = getConversationBoundTime(end);
	if(startMs == null || endMs == null) {
		return { start, end };
	}

	let sampleDelta = null;
	let earliestClock = Infinity;
	(messages || []).forEach(message => {
		const clockMs = getWrittenClockMs(message && message.date);
		if(clockMs == null || clockMs >= earliestClock) {
			return;
		}
		// Le premier message est celui qui a produit la borne de début.
		// « Le plus proche » se trompe quand le décalage est négatif : un message
		// plus tardif est alors numériquement plus près de l'instant enregistré.
		earliestClock = clockMs;
		sampleDelta = clockMs - startMs;
	});

	if(
		sampleDelta == null
		|| sampleDelta === 0
		|| Math.abs(sampleDelta) > IMPORTED_CLOCK_SHIFT_MAX_MS
		|| sampleDelta % IMPORTED_CLOCK_SHIFT_STEP_MS !== 0
	) {
		return { start, end };
	}

	const endAligned = endMs + sampleDelta;
	const endMatches = (messages || []).some(message => {
		const clockMs = getWrittenClockMs(message && message.date);
		return clockMs != null && Math.abs(clockMs - endAligned) <= 60 * 1000;
	});
	if(!endMatches) {
		return { start, end };
	}

	return {
		start: new Date(startMs + sampleDelta),
		end: new Date(endAligned),
	};
}

export function resolveConversationDateBounds(dates = {}, messagesBlocks = []) {
	const storedStartMs = getConversationBoundTime(dates.start);
	const storedEndMs = getConversationBoundTime(dates.end);
	const storedPairValid = storedStartMs != null && storedEndMs != null && storedEndMs >= storedStartMs;

	if(storedPairValid) {
		return {
			start: dates.start,
			end: dates.end,
		};
	}

	let blockStartMs = null;
	let blockEndMs = null;
	(messagesBlocks || []).forEach(block => {
		const startMs = getConversationBoundTime(block && block.start);
		const endMs = getConversationBoundTime(block && block.end);
		if(startMs != null && (blockStartMs == null || startMs < blockStartMs)) {
			blockStartMs = startMs;
		}
		if(endMs != null && (blockEndMs == null || endMs > blockEndMs)) {
			blockEndMs = endMs;
		}
		if(startMs != null && (blockEndMs == null || startMs > blockEndMs)) {
			blockEndMs = startMs;
		}
	});

	const startMs = blockStartMs ?? storedStartMs;
	let endMs = blockEndMs ?? storedEndMs;
	if(startMs != null && endMs != null && endMs < startMs) {
		endMs = null;
	}

	return {
		start: startMs == null ? undefined : new Date(startMs),
		end: endMs == null ? undefined : new Date(endMs),
	};
}

export function sortMessagesByDate(messages = []) {
	return (messages || [])
		.map((message, index) => ({ message, index, time: getMessageTime(message) }))
		.sort((entryA, entryB) => {
			if(entryA.time === null || entryB.time === null) {
				// Dates invalides en fin de liste, dans leur ordre d'origine.
				if(entryA.time === entryB.time) {
					return entryA.index - entryB.index;
				}
				return entryA.time === null ? 1 : -1;
			}
			return (entryA.time - entryB.time) || (entryA.index - entryB.index);
		})
		.map(entry => entry.message);
}
